/* eslint-disable react/prop-types */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, FileText, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import SearchPicker from "../../../../components/admin/SearchPicker.jsx";
import { SelectDropdown, StatusBadge, TextInput, confirmDelete, showErrorAlert } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import { canManageAllProjects } from "../../../../lib/permissions.js";
import { CUSTOMER_STATE } from "../../../../components/catalog/catalogUtils.js";
import {
    BackButton, ContactActions, CrmAvatar, CrmDetailSkeleton, CrmField, CrmInput, CrmSection, EditBar,
} from "../../../../components/crm/CrmParts.jsx";
import CrmActivityPanel from "../../../../components/crm/CrmActivityPanel.jsx";
import {
    CLIENT_STATUSES, PHONE_TYPES, SOCIAL_PLATFORMS, apiErrorText, clienteName, formatDate, labelFor, optionFor, userLabel,
} from "../../../../components/crm/crmUtils.js";

const EMPTY = {
    nombre: "", apellido: "", company_name: "", email: "", web: "", direccion: "", status: "LEAD",
    user: null, user_label: "", assigned_to: null, assigned_to_label: "", telefonos: [], redes_sociales: [],
};

const draftFrom = (cliente) => ({
    nombre: cliente.nombre || "",
    apellido: cliente.apellido || "",
    company_name: cliente.company_name || "",
    email: cliente.email || "",
    web: cliente.web || "",
    direccion: cliente.direccion || "",
    status: cliente.status || "LEAD",
    user: cliente.user || null,
    user_label: userLabel(cliente.user_info),
    assigned_to: cliente.assigned_to || null,
    assigned_to_label: cliente.assigned_to_name || "",
    telefonos: (cliente.telefonos || []).map(({ tipo, numero }) => ({ tipo, numero })),
    redes_sociales: (cliente.redes_sociales || []).map(({ plataforma, url }) => ({ plataforma, url })),
});

function Attachments({ cliente, onChanged }) {
    const [file, setFile] = useState(null);
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);

    const upload = async () => {
        setBusy(true);
        try {
            const form = new FormData();
            form.append("file", file);
            form.append("descripcion", description);
            await axios.post(`/clients/${cliente.id}/attachments/`, form, { headers: { "Content-Type": "multipart/form-data" } });
            setFile(null);
            setDescription("");
            onChanged();
        } catch (error) {
            showErrorAlert({ text: apiErrorText(error, "No se ha podido subir el archivo.") });
        } finally {
            setBusy(false);
        }
    };

    const remove = async (attachment) => {
        const confirmed = await confirmDelete({ title: "Borrar adjunto", text: attachment.descripcion || attachment.file?.split("/").pop(), confirmButtonText: "Borrar" });
        if (!confirmed) return;
        try {
            await axios.delete(`/clients/${cliente.id}/attachments/${attachment.id}/`);
            onChanged();
        } catch {
            showErrorAlert({ text: "No se ha podido borrar el adjunto." });
        }
    };

    const attachments = cliente.attachments || [];
    return (
        <CrmSection title="Adjuntos">
            {attachments.length === 0 ? (
                <p className="text-sm text-base-content/60">Sin adjuntos. Sube presupuestos, logos o lo que te mande el cliente.</p>
            ) : (
                <ul className="flex flex-col gap-1">
                    {attachments.map((attachment) => (
                        <li key={attachment.id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-base-200">
                            <FileText className="w-4 h-4 shrink-0 text-base-content/60" aria-hidden="true" />
                            <a href={getMediaUrl(attachment.file)} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 text-sm truncate link-hover">
                                {attachment.descripcion || attachment.file?.split("/").pop()}
                            </a>
                            <span className="text-xs text-base-content/50 shrink-0">{formatDate(attachment.uploaded_at)}</span>
                            <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={() => remove(attachment)} aria-label="Borrar adjunto">
                                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex flex-col gap-2 border-t border-base-content/10 pt-3">
                <input
                    type="file"
                    className="file-input file-input-bordered file-input-sm w-full"
                    onChange={(event) => setFile(event.target.files?.[0] || null)}
                    aria-label="Archivo a adjuntar"
                />
                {file && (
                    <div className="flex gap-2">
                        <TextInput inputSize="sm" placeholder="Descripción (opcional)" value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Descripción del adjunto" />
                        <button type="button" className="btn btn-primary btn-sm gap-1" onClick={upload} disabled={busy}>
                            {busy ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : <Upload className="w-4 h-4" aria-hidden="true" />}
                            Subir
                        </button>
                    </div>
                )}
            </div>
        </CrmSection>
    );
}

function ProspectLogos({ logos, name }) {
    if (!logos?.length) return null;
    return (
        <CrmSection title="Imágenes de prospección">
            <div className="grid grid-cols-2 gap-2">
                {logos.map((logo) => (
                    <a
                        key={logo.lead_id}
                        href={getMediaUrl(logo.file)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group rounded-lg border border-base-content/10 bg-base-200 p-2 flex flex-col gap-1"
                        title="Abrir a tamaño completo"
                    >
                        <img src={getMediaUrl(logo.file)} alt={`Logo de ${logo.company_name || name}`} className="h-24 w-full object-contain" loading="lazy" />
                        <span className="text-xs text-base-content/60 truncate flex items-center gap-1">
                            <ExternalLink className="w-3 h-3 shrink-0" aria-hidden="true" />
                            {logo.logo_width && logo.logo_height ? `${logo.logo_width} × ${logo.logo_height} px` : "Abrir"}
                        </span>
                    </a>
                ))}
            </div>
        </CrmSection>
    );
}

function RepeatList({ items, onChange, renderRow, addLabel, emptyItem }) {
    const update = (index, patch) => onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    return (
        <div className="flex flex-col gap-2">
            {items.map((item, index) => (
                <div key={index} className="flex gap-2 items-start">
                    <div className="flex-1 grid gap-2 sm:grid-cols-[10rem_1fr]">{renderRow(item, (patch) => update(index, patch))}</div>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => onChange(items.filter((_, i) => i !== index))} aria-label="Quitar">
                        <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                </div>
            ))}
            <button type="button" className="btn btn-ghost btn-xs gap-1 self-start" onClick={() => onChange([...items, { ...emptyItem }])}>
                <Plus className="w-3.5 h-3.5" aria-hidden="true" /> {addLabel}
            </button>
        </div>
    );
}

/**
 * Ficha de un cliente en una sola página (C3D-46): se lee y se edita en el
 * mismo sitio. Sin `clienteId` es el alta de uno nuevo.
 */
export default function ClienteFicha({ clienteId, onBack, onSaved, onDeleted }) {
    const seesAll = canManageAllProjects(useAuthContext());
    const isNew = !clienteId;
    const [cliente, setCliente] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [editing, setEditing] = useState(isNew);
    const [draft, setDraft] = useState(EMPTY);
    const [errors, setErrors] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activityKey, setActivityKey] = useState(0);

    const load = useCallback(() => {
        if (isNew) return Promise.resolve();
        setLoadError(null);
        return axios.get(`/clients/${clienteId}/`)
            .then(({ data }) => { setCliente(data); setDraft(draftFrom(data)); })
            .catch((error) => setLoadError(error?.response?.status === 404 ? "not_found" : "network"));
    }, [clienteId, isNew]);
    useEffect(() => { load(); }, [load]);

    if (loadError) {
        return (
            <div className="p-4 flex flex-col items-start gap-3">
                <p role="alert" className="text-error">{loadError === "not_found" ? "Este cliente no existe o no lo llevas tú." : "No se ha podido cargar el cliente."}</p>
                <div className="flex gap-2">
                    <button type="button" className="btn btn-sm" onClick={onBack}>Volver a clientes</button>
                    {loadError !== "not_found" && <button type="button" className="btn btn-sm btn-primary" onClick={load}>Reintentar</button>}
                </div>
            </div>
        );
    }
    if (!isNew && !cliente) return <CrmDetailSkeleton />;

    const set = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));
    const original = isNew ? EMPTY : draftFrom(cliente);
    const dirty = JSON.stringify(draft) !== JSON.stringify(original);
    const fieldError = (field) => errors?.[field];

    const save = async () => {
        setSaving(true);
        setErrors(null);
        const payload = {
            nombre: draft.nombre,
            apellido: draft.apellido,
            company_name: draft.company_name,
            email: draft.email,
            web: draft.web,
            direccion: draft.direccion,
            status: draft.status,
            user: draft.user || null,
            telefonos: draft.telefonos.filter((phone) => phone.numero.trim()),
            redes_sociales: draft.redes_sociales.filter((network) => network.url.trim()),
            ...(seesAll ? { assigned_to: draft.assigned_to || null } : {}),
        };
        try {
            const { data } = isNew
                ? await axios.post("/clients/", payload)
                : await axios.patch(`/clients/${clienteId}/`, payload);
            if (isNew) {
                onSaved(data.id);
                return;
            }
            setCliente(data);
            setDraft(draftFrom(data));
            setEditing(false);
        } catch (error) {
            const data = error?.response?.data;
            setErrors(data && typeof data === "object" ? data : { detail: apiErrorText(error, "No se ha podido guardar.") });
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        if (isNew) { onBack(); return; }
        setDraft(draftFrom(cliente));
        setErrors(null);
        setEditing(false);
    };

    const remove = async () => {
        const confirmed = await confirmDelete({
            title: "Borrar cliente",
            text: `Se borra ${clienteName(cliente)} con sus teléfonos y adjuntos. Sus proyectos y pedidos se quedan.`,
            confirmButtonText: "Borrar cliente",
        });
        if (!confirmed) return;
        try {
            await axios.delete(`/clients/${clienteId}/`);
            onDeleted();
        } catch {
            showErrorAlert({ text: "No se ha podido borrar el cliente." });
        }
    };

    const name = isNew ? (clienteName(draft) || "Nuevo cliente") : clienteName(cliente);
    const status = optionFor(CLIENT_STATUSES, cliente?.status);
    const state = cliente ? CUSTOMER_STATE[cliente.customer_state] : null;
    const firstPhone = cliente?.telefonos?.[0]?.numero;
    const logo = cliente?.prospect_logos?.[0]?.file;
    const generalError = errors && (errors.detail || errors.non_field_errors);
    const newProjectParam = cliente ? `cliente:${cliente.id}&nombre=${encodeURIComponent(name)}` : "";

    return (
        <div className="p-2 sm:p-4 flex flex-col gap-4">
            <BackButton label="Clientes" onClick={onBack} />

            <header className="rounded-xl border border-base-content/10 bg-base-100 p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4 min-w-0">
                    <CrmAvatar name={name} imageUrl={logo} size="lg" />
                    <div className="flex flex-col gap-1.5 min-w-0">
                        <h1 className="text-2xl font-bold leading-tight truncate">{name}</h1>
                        {!isNew && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {status && <StatusBadge label={status.label} tone={status.tone} size="sm" />}
                                {state && <StatusBadge label={cliente.customer_state === "cliente" ? "Ha comprado" : state.label} tone={state.tone} title={state.title} size="sm" outline />}
                                {cliente.assigned_to_name && <span className="text-xs text-base-content/60">Comercial: {cliente.assigned_to_name}</span>}
                            </div>
                        )}
                    </div>
                </div>
                {!isNew && !editing && (
                    <div className="flex flex-wrap gap-2 md:justify-end">
                        <ContactActions phone={firstPhone} email={cliente.email} website={cliente.web} />
                        <button type="button" className="btn btn-outline btn-sm gap-1.5" onClick={() => setEditing(true)}>
                            <Pencil className="w-4 h-4" aria-hidden="true" /> Editar
                        </button>
                    </div>
                )}
            </header>

            <div className={`grid gap-4 items-start ${isNew ? "" : "lg:grid-cols-[1fr_22rem]"}`}>
                <div className="flex flex-col gap-4 min-w-0">
                    {generalError && <div role="alert" className="alert alert-error text-sm">{String(generalError)}</div>}

                    <CrmSection title="Datos">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-2">
                                <CrmInput label="Nombre" error={fieldError("nombre")}>
                                    <TextInput value={draft.nombre} onChange={(e) => set("nombre", e.target.value)} required autoFocus={isNew} />
                                </CrmInput>
                                <CrmInput label="Apellidos" error={fieldError("apellido")}>
                                    <TextInput value={draft.apellido} onChange={(e) => set("apellido", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Empresa" error={fieldError("company_name")}>
                                    <TextInput value={draft.company_name} onChange={(e) => set("company_name", e.target.value)} />
                                </CrmInput>
                                <CrmInput label="Email" error={fieldError("email")}>
                                    <TextInput type="email" value={draft.email} onChange={(e) => set("email", e.target.value)} required />
                                </CrmInput>
                                <CrmInput label="Web" error={fieldError("web")} help="Vale sin https://">
                                    <TextInput value={draft.web} onChange={(e) => set("web", e.target.value)} placeholder="www.ejemplo.com" />
                                </CrmInput>
                                <CrmInput label="Dirección" error={fieldError("direccion")}>
                                    <TextInput value={draft.direccion} onChange={(e) => set("direccion", e.target.value)} />
                                </CrmInput>
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <CrmField label="Nombre">{[cliente.nombre, cliente.apellido].filter(Boolean).join(" ")}</CrmField>
                                <CrmField label="Empresa">{cliente.company_name}</CrmField>
                                <CrmField label="Email">{cliente.email}</CrmField>
                                <CrmField label="Web">{cliente.web && <a className="link link-hover" href={cliente.web} target="_blank" rel="noopener noreferrer">{cliente.web.replace(/^https?:\/\//, "")}</a>}</CrmField>
                                <CrmField label="Dirección" className="sm:col-span-2">{cliente.direccion}</CrmField>
                                <CrmField label="Alta">{formatDate(cliente.created_at)}</CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    <CrmSection title="Teléfonos y redes">
                        {editing ? (
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="flex flex-col gap-2">
                                    <span className="label-text text-sm font-medium">Teléfonos</span>
                                    <RepeatList
                                        items={draft.telefonos}
                                        onChange={(value) => set("telefonos", value)}
                                        addLabel="Añadir teléfono"
                                        emptyItem={{ tipo: "MOBILE", numero: "" }}
                                        renderRow={(phone, update) => (
                                            <>
                                                <SelectDropdown options={PHONE_TYPES} value={phone.tipo} onChange={(tipo) => update({ tipo })} size="sm" className="w-full" />
                                                <TextInput inputSize="sm" value={phone.numero} onChange={(e) => update({ numero: e.target.value })} placeholder="600 000 000" aria-label="Número" />
                                            </>
                                        )}
                                    />
                                </div>
                                <div className="flex flex-col gap-2">
                                    <span className="label-text text-sm font-medium">Redes sociales</span>
                                    <RepeatList
                                        items={draft.redes_sociales}
                                        onChange={(value) => set("redes_sociales", value)}
                                        addLabel="Añadir red"
                                        emptyItem={{ plataforma: "INSTAGRAM", url: "" }}
                                        renderRow={(network, update) => (
                                            <>
                                                <SelectDropdown options={SOCIAL_PLATFORMS} value={network.plataforma} onChange={(plataforma) => update({ plataforma })} size="sm" className="w-full" />
                                                <TextInput inputSize="sm" value={network.url} onChange={(e) => update({ url: e.target.value })} placeholder="https://instagram.com/..." aria-label="Enlace" />
                                            </>
                                        )}
                                    />
                                    {fieldError("redes_sociales") && <span className="text-xs text-error">Revisa los enlaces: tienen que ser direcciones completas.</span>}
                                </div>
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-2">
                                <CrmField label="Teléfonos">
                                    {cliente.telefonos?.length ? (
                                        <ul className="flex flex-col gap-0.5">
                                            {cliente.telefonos.map((phone) => (
                                                <li key={phone.id}><span className="text-base-content/60">{labelFor(PHONE_TYPES, phone.tipo)}:</span> {phone.numero}</li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </CrmField>
                                <CrmField label="Redes sociales">
                                    {cliente.redes_sociales?.length ? (
                                        <ul className="flex flex-col gap-0.5">
                                            {cliente.redes_sociales.map((network) => (
                                                <li key={network.id}>
                                                    <a className="link link-hover" href={network.url} target="_blank" rel="noopener noreferrer">{labelFor(SOCIAL_PLATFORMS, network.plataforma)}</a>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    <CrmSection title="Gestión">
                        {editing ? (
                            <div className="grid gap-3 md:grid-cols-3">
                                <div className="form-control gap-1">
                                    <span className="label-text text-sm font-medium">Estado</span>
                                    <SelectDropdown options={CLIENT_STATUSES} value={draft.status} onChange={(value) => set("status", value)} className="w-full" />
                                </div>
                                <SearchPicker
                                    label="Usuario registrado"
                                    value={draft.user}
                                    selectedLabel={draft.user_label}
                                    endpoint="/projects/people-options/"
                                    paramsForQuery={(query) => ({ kind: "customer", search: query || undefined })}
                                    getOptionLabel={userLabel}
                                    onSelect={(option) => setDraft((prev) => ({ ...prev, user: option?.id || null, user_label: userLabel(option) }))}
                                />
                                {seesAll && (
                                    <SearchPicker
                                        label="Comercial"
                                        value={draft.assigned_to}
                                        selectedLabel={draft.assigned_to_label}
                                        endpoint="/projects/people-options/"
                                        paramsForQuery={(query) => ({ kind: "sales", search: query || undefined })}
                                        getOptionLabel={userLabel}
                                        onSelect={(option) => setDraft((prev) => ({ ...prev, assigned_to: option?.id || null, assigned_to_label: userLabel(option) }))}
                                    />
                                )}
                            </div>
                        ) : (
                            <dl className="grid gap-4 sm:grid-cols-3">
                                <CrmField label="Estado">{status?.label}</CrmField>
                                <CrmField label="Usuario registrado">{userLabel(cliente.user_info)}</CrmField>
                                <CrmField label="Comercial">{cliente.assigned_to_name}</CrmField>
                            </dl>
                        )}
                    </CrmSection>

                    {editing && <EditBar saving={saving} dirty={dirty} onCancel={cancel} onSave={save} saveLabel={isNew ? "Crear cliente" : "Guardar"} />}

                    {!isNew && !editing && (
                        <div className="flex justify-end">
                            <button type="button" className="btn btn-ghost btn-sm gap-1 text-error" onClick={remove}>
                                <Trash2 className="w-4 h-4" aria-hidden="true" /> Borrar cliente
                            </button>
                        </div>
                    )}
                </div>

                {!isNew && (
                    <aside className="flex flex-col gap-4 lg:sticky lg:top-2">
                        <CrmActivityPanel endpoint={`/clients/${cliente.id}/`} newProjectParam={newProjectParam} reloadKey={activityKey} />
                        <Attachments cliente={cliente} onChanged={() => { load(); setActivityKey((k) => k + 1); }} />
                        <ProspectLogos logos={cliente.prospect_logos} name={name} />
                    </aside>
                )}
            </div>
        </div>
    );
}
